import {
    atom,
    selector,
    useRecoilValue,
    useRecoilState,
    useSetRecoilState,
} from 'recoil';
import { AdjustmentType, DataType } from 'enums';
import {
    convertDisputeSummaries,
    convertInvoiceSummaries,
    convertPaymentDetails,
    convertPaymentSummaries,
    validateSearch,
} from 'functions';
import { SearchService } from 'services';
import { isAuthenticated, refreshState, useSetRefreshState } from 'state';
import { AdjustmentDetail, SearchResult } from 'types';
import { accountId } from './account';
import { localStorageEffect } from './effects';

export const searchQuery = atom<string>({
    key: 'searchQuery',
    default: undefined,
    effects: [localStorageEffect('LastSearch')],
});

const adjustmentToDataType = ({ adjustmentTypeId }: AdjustmentDetail): DataType => {
    switch (adjustmentTypeId) {
        case AdjustmentType.CustomerDeduction: return DataType.CustomerDeduction;
        case AdjustmentType.DotBillback: return DataType.DotBillback;
        case AdjustmentType.DotCreditToSupplier: return DataType.Credit;
        case AdjustmentType.CustomerDedRepay: return DataType.CustomerDedRepay;
        default: return DataType.Unknown;
    }
};

export const searchResults = selector<SearchResult[]>({
    key: 'searchResults',
    get: async ({ get }) => {
        get(refreshState(searchResults.key));
        const searchAccountId = get(accountId);
        const query = get(searchQuery);

        const { isValid } = validateSearch(query, searchAccountId);
        if (!get(isAuthenticated) || !query || !isValid) return [];

        const service = new SearchService();
        const encodedQuery = encodeURIComponent(query);
        
        let results;
        results = await service.getSearchResults(searchAccountId, encodedQuery);
        if (!results) return [];

        const { adjustments, disputes, invoices, payments } = results;
        return [
            ...convertPaymentDetails(adjustments).map((data) => ({
                type: adjustmentToDataType(data),
                data,
            })),
            ...convertDisputeSummaries(disputes).map((data) => ({
                type: DataType.Dispute,
                data,
            })),
            ...convertInvoiceSummaries(invoices).map((data) => ({
                type: DataType.Invoice,
                data,
            })),
            ...convertPaymentSummaries(payments).map((data) => ({
                type: DataType.Payment,
                data,
            })),
        ];
    },
});

export const useSearchQuery = () => useRecoilState(searchQuery);

export const useSetSearchQuery = () => useSetRecoilState(searchQuery);

export const useSearchResults = () => useRecoilValue(searchResults);

export const useRefreshSearchResults = () => useSetRefreshState(searchResults);
